/**
 * conversationContract.js
 *
 * Single source of truth for the voice "brain" <-> engine contract.
 *
 * Why this module exists
 * ----------------------
 * Three voice engines (Classic, Gemini Live, OpenAI Realtime) drive the same
 * symptom-checker brain (`onPauseDetected`). Historically each engine and the
 * brain re-implemented intent parsing, "latest user turn" resolution, and the
 * completion result shape, which caused drift (e.g. verbal "stop" handled
 * differently per engine). This module centralizes:
 *
 *   1. The COMPLETION RESULT shape returned by the brain to any engine.
 *   2. "Latest user turn" resolution that is backend-agnostic.
 *   3. Continue/stop intent parsing used at phase gates.
 *   4. Explicit "stop the whole conversation" intent detection.
 *
 * Engine contract (all engines MUST honor):
 * - Brain receives { userTranscript, conversationLog, questionsAsked, context }
 *   where `userTranscript` is the LATEST user turn (not a cumulative join).
 * - Brain returns a CONVERSATION RESULT (see makeConversationResult):
 *     { question, isComplete, stopImmediately }
 *   - question:        next line to speak (may be '').
 *   - isComplete:      conversation is finished; engine should tear down and
 *                      fire onComplete after any final line is delivered.
 *   - stopImmediately: end NOW without speaking a closing line (verbal/explicit
 *                      stop). Engine must stop mic + fire onComplete promptly.
 */

import { getLastUserResponse } from './helpers';
import { MEDICOLEGAL_DISCLAIMER } from './constants';

/**
 * Affirmative / negative synonym sets for the continue/stop phase gate.
 * Keep these in one place so every surface recognizes the same words.
 *
 * Affirmative: yes, yeah, yep, sure, ok/okay, continue, go ahead, proceed,
 *              let's continue, keep going, absolutely, please continue
 * Negative:    no, nope, stop, end, finish, done, that's all, quit, exit
 */
const CONTINUE_PATTERN =
  /\b(continue|go ahead|keep going|proceed|yes|yep|yeah|sure|okay|ok|let'?s continue|absolutely|please continue)\b/i;
const STOP_PATTERN =
  /\b(stop|end|finish|done|that's all|that is all|no|nope|quit|exit)\b/i;
/** Gate-specific stop phrases (e.g. "stop here" after Symptom Discovery). */
const STOP_AT_GATE_PATTERN =
  /\b(stop here|end here|stop for now|stop now|that's enough|stay here and stop)\b/i;

/**
 * Resolve the latest user turn in a backend-agnostic way.
 *
 * Gemini Live passes the latest turn via `currentTranscript` BEFORE it is
 * appended to `conversationLog`; Classic appends first. Preferring
 * `currentTranscript` (when present) makes gate logic identical across engines.
 *
 * @param {Array<{speaker:string,text:string}>} conversationLog
 * @param {string} currentTranscript
 * @returns {string}
 */
export function resolveLatestUserTurn(conversationLog, currentTranscript) {
  const latestTurn = String(currentTranscript || '').trim();
  if (latestTurn) return latestTurn;
  return getLastUserResponse(conversationLog || []);
}

/**
 * Continue/stop gate decision.
 * @returns {'confirmed'|'denied'|'unknown'}
 */
export function userWantsToContinue(text) {
  if (!text) return 'unknown';
  const lower = String(text).toLowerCase().trim();
  // Affirmative first so phrases like "no, continue" still advance.
  if (CONTINUE_PATTERN.test(lower)) return 'confirmed';
  if (STOP_PATTERN.test(lower) || STOP_AT_GATE_PATTERN.test(lower)) return 'denied';
  return 'unknown';
}

/** Strip punctuation so STT variants like "STOP HERE." still match. */
export function normalizeStopIntentText(text) {
  return String(text || '')
    .toLowerCase()
    .replace(/[^\w\s']/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const STOP_CONVERSATION_EXACT = new Set([
  'stop',
  'stop now',
  'stop here',
  'end',
  'end now',
  'end here',
  'end conversation',
  'quit',
  'exit',
  'done',
  "i'm done",
  'im done',
  "that's all",
  'that is all',
  'no thanks',
  'stop for now',
  'stop the conversation',
  'end the conversation',
]);

const STOP_CONVERSATION_PATTERNS = [
  STOP_AT_GATE_PATTERN,
  /\b(stop|end|quit|exit)\s+(the\s+)?(conversation|chat|session)\b/i,
  /\b(i\s+(want|would like|need|wanna)\s+to)\s+(stop|end|quit|exit)\b/i,
  /\blet'?s\s+(stop|end|quit|exit)\b/i,
  /\b(stop|end)\s+(here|now|for now)\b/i,
  /\b(that'?s enough|stay here and stop)\b/i,
];

/**
 * True when the user explicitly asked to end the whole conversation (not just
 * answer a gate). Used to end immediately without a scripted closing line.
 */
export function isStopConversationIntent(text) {
  const normalized = normalizeStopIntentText(text);
  if (!normalized) return false;
  if (STOP_CONVERSATION_EXACT.has(normalized)) return true;
  return STOP_CONVERSATION_PATTERNS.some((re) => re.test(normalized));
}

/**
 * True when the user wants to end now — explicit stop phrases anywhere, or
 * continue/stop gate negatives while a stage gate is pending.
 *
 * @param {string} text
 * @param {{ atStageGate?: boolean }} [options]
 */
export function shouldEndConversationFromUserSpeech(text, { atStageGate = false } = {}) {
  if (isStopConversationIntent(text)) return true;
  if (atStageGate && userWantsToContinue(text) === 'denied') return true;
  return false;
}

/**
 * Build a normalized CONVERSATION RESULT for the engine.
 * Every brain return path should go through this so the shape never drifts.
 *
 * @param {string} question
 * @param {object} [opts]
 * @param {boolean} [opts.isComplete=false]
 * @param {boolean} [opts.stopImmediately=false]
 */
export function makeConversationResult(
  question,
  {
    isComplete = false,
    stopImmediately = false,
    preserveExactText = false,
    deferredUserTranscript = '',
  } = {}
) {
  return {
    question: question || '',
    isComplete: Boolean(isComplete),
    stopImmediately: Boolean(stopImmediately),
    preserveExactText: Boolean(preserveExactText),
    deferredUserTranscript: String(deferredUserTranscript || ''),
  };
}

/**
 * Canonical medicolegal disclaimer line (spoken once before opening).
 * @param {{ medicolegalDisclaimer?: string } | null | undefined} prompts
 * @returns {string}
 */
export function getMedicolegalDisclaimer(prompts) {
  const configured = prompts?.medicolegalDisclaimer;
  if (typeof configured === 'string' && configured.trim()) {
    return configured.trim();
  }
  return MEDICOLEGAL_DISCLAIMER;
}

/**
 * True when assistant text is the medicolegal disclaimer (not an urgent-care advisory).
 * @param {string} text
 * @returns {boolean}
 */
export function isMedicolegalDisclaimerText(text) {
  const normalized = String(text || '').trim();
  if (!normalized) return false;
  if (normalized.startsWith(MEDICOLEGAL_DISCLAIMER)) return true;
  return normalized.includes('This tool provides general health information only');
}

/** Estimated spoken seconds for disclaimer mic gating (Gemini Live). */
export const MEDICOLEGAL_DISCLAIMER_ESTIMATED_SPEECH_S = 14;

/**
 * Merge assistant transcription chunks without regressing to shorter partial text.
 * Used by Gemini Live output transcription (final-only log commits use this buffer).
 *
 * @param {string} prior
 * @param {string} incoming
 * @param {(a: string, b: string) => boolean} [sameUtteranceFn]
 * @returns {string}
 */
export function mergeAssistantTranscriptionChunk(prior, incoming, sameUtteranceFn) {
  const priorText = String(prior || '').trim();
  const incomingText = String(incoming || '').trim();
  if (!incomingText) return priorText;
  if (!priorText) return incomingText;
  if (incomingText.startsWith(priorText)) return incomingText;
  if (priorText.startsWith(incomingText)) return priorText;
  if (typeof sameUtteranceFn === 'function' && sameUtteranceFn(priorText, incomingText)) {
    return priorText.length >= incomingText.length ? priorText : incomingText;
  }
  // Unrelated partial (e.g. policy disclaimer streaming): never shrink visible text.
  return priorText.length >= incomingText.length ? priorText : incomingText;
}

const conversationContract = {
  resolveLatestUserTurn,
  userWantsToContinue,
  normalizeStopIntentText,
  isStopConversationIntent,
  shouldEndConversationFromUserSpeech,
  makeConversationResult,
  getMedicolegalDisclaimer,
  isMedicolegalDisclaimerText,
  mergeAssistantTranscriptionChunk,
};

export default conversationContract;
